import { useQuery } from '@apollo/client/react';
import { useFocusEffect } from 'expo-router';
import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';

import { SettingsNavigationItem } from '@/components/molecules/list';
import { formatMeetingSummaryQuotaSubtitle } from '@/formatters/formatMeetingSummaryQuotaUsage';
import { MeetingSummaryQuotaUsageDocument } from '@/generated/usermobile-bff/graphql';
import { useTranslation } from '@/i18n';
import { spacing, unisonColors } from '@/theme';

interface SettingsMenuListProps {
  readonly onNavigate: (view: string) => void;
}

interface SettingsMenuItemProps {
  readonly title: string;
  readonly description?: string;
  readonly onPress: () => void;
  readonly testID: string;
}

const SettingsMenuItem = ({ title, description, onPress, testID }: SettingsMenuItemProps) => {
  return (
    <View style={styles.card} testID={`${testID}-card`}>
      <SettingsNavigationItem
        description={description}
        onPress={onPress}
        testID={testID}
        title={title}
      />
    </View>
  );
};

export const SettingsMenuList = ({ onNavigate }: SettingsMenuListProps) => {
  const { t, locale } = useTranslation();
  const { data, loading, refetch } = useQuery(MeetingSummaryQuotaUsageDocument, {
    fetchPolicy: 'cache-and-network',
  });

  useFocusEffect(
    useCallback(() => {
      void refetch();
    }, [refetch]),
  );

  const quotaDescription = loading
    ? '...'
    : formatMeetingSummaryQuotaSubtitle({
        locale,
        usedMinutes: data?.meetingSummaryQuotaUsage.usedMinutes ?? 0,
        allowanceMinutes: data?.meetingSummaryQuotaUsage.totalMinutes ?? 0,
        t,
      });

  const items = [
    {
      testID: 'settings-menu-item-network',
      title: t('settings.networkAndMobileData'),
      view: 'network',
    },
    {
      testID: 'settings-menu-item-quotaUsage',
      title: t('settings.quotaUsage.title'),
      description: quotaDescription,
      view: 'quotaUsage',
    },
    {
      testID: 'settings-menu-item-storage',
      title: t('settings.localMeetingRecordings'),
      view: 'storage',
    },
  ];

  return (
    <View style={styles.container}>
      {items.map((item) => (
        <SettingsMenuItem
          key={item.view}
          testID={item.testID}
          title={item.title}
          description={item.description}
          onPress={() => onNavigate(item.view)}
        />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing[2],
  },
  card: {
    backgroundColor: unisonColors.white,
    borderRadius: 8,
  },
});
